import { memo } from 'react'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatChange } from '../../../../shared/lib/analytics-format'

/**
 * 기간 요약.
 *
 * 세 지표를 한 장 안에 칸으로 나눠 둔다. 따로 떼어 놓으면 서로 다른
 * 것처럼 보이는데, 셋 다 같은 기간을 설명하는 값이다.
 */

export interface SummaryCell {
  label: string
  value: string
  change: number
  hint: string
}

interface AnalyticsSummaryProps {
  cells: SummaryCell[]
}

function AnalyticsSummary({ cells }: AnalyticsSummaryProps) {
  const { t } = useTranslation()

  return (
    <section
      aria-label={t('analyticsPage.periodClicks')}
      className="flex flex-wrap overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
      {cells.map((cell, index) => {
        const rising = cell.change > 0
        const flat = cell.change === 0
        const Icon = rising ? ArrowUpRight : ArrowDownRight

        return (
          <div
            key={cell.label}
            className={`flex min-w-0 flex-[1_1_200px] flex-col gap-1.5 px-6 py-5 ${
              index < cells.length - 1
                ? 'border-b border-gray-100 dark:border-gray-700 sm:border-b-0 sm:border-r'
                : ''
            }`}
          >
            <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
              {cell.label}
            </span>
            <span className="text-[34px] font-extrabold tabular-nums leading-none tracking-tight text-gray-900 dark:text-white">
              {cell.value}
            </span>
            <span className="flex flex-wrap items-center gap-1.5 text-[13px] text-gray-400 dark:text-gray-500">
              {!flat && (
                <span
                  className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold tabular-nums ${
                    rising
                      ? 'bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-400'
                      : 'bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400'
                  }`}
                >
                  <Icon className="h-3 w-3" />
                  {formatChange(cell.change)}
                </span>
              )}
              {cell.hint}
            </span>
          </div>
        )
      })}
    </section>
  )
}

export default memo(AnalyticsSummary)
